"use client";
import * as S from "./styles.ts";
import {
  Accordion2,
  Button,
  H1,
  IconBulletPoint,
  P,
  UniversalLink,
  VideoFrame,
} from "@template/components";
import Image, { ImageProps } from "@template/components/Image";
import { IButtonProps } from "@template/components/Button";
import UniversalMarkdown from "@template/blocks/universal-markdown.tsx";
import Quotes from "./Quotes/index.tsx";
import { useEffect, useRef, useState } from "react";
import Quote from "@/lib/images/about/Quote.svg";

interface IQuotes {
  mainQuote?: string;
  quotedPerson?: string;
  company?: string;
  link?: string;
}
export interface IBasicSectionProps {
  button?: {
    href: string;
  } & IButtonProps;
  header?: { title: string; subtitle?: string };
  description?: string;
  descriptionCtaText?: { label: string; href?: string }[];
  image?: ImageProps;
  videoUrl?: string;
  subtitle?: string;
  title?: string;
  flip?: boolean;
  bullets?: {
    text: string;
  }[];
  bulletsWithIcon?: boolean;
  accordionData?: { content: string; title: string }[];
  linkContent?: {
    label: string;
    href: string;
    target?: "_blank" | "_self";
    title?: string;
  }[];
  accordionHeader?: string;
  inverted?: boolean;
  anchorId?: string;
  quotes?: IQuotes[];
  contentType?: "image" | "video" | "quotes";
}

const BasicSectionComponent = ({
  title,
  subtitle,
  button,
  description,
  descriptionCtaText,
  image,
  videoUrl,
  flip,
  bullets,
  bulletsWithIcon = true,
  accordionData,
  linkContent,
  accordionHeader,
  header,
  inverted = false,
  anchorId,
  quotes,
  contentType,
}: IBasicSectionProps) => {
  const [current, setCurrent] = useState(0);
  const intervalTime = 5000;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const resetInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % quotes?.length);
    }, intervalTime);
  };

  useEffect(() => {
    resetInterval(); // start the interval when component mounts
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current); // cleanup on unmount
    };
  }, []);

  const handleArrowClick = (newIndex: number) => {
    setCurrent(newIndex);
    resetInterval(); // reset interval on arrow click
  };
  return (
    <S.ItemWrapper
      $inverted={inverted}
      id={anchorId}
    >
      {(header?.title || header?.subtitle) && (
        <S.HeaderContainer>
          {header.title && <H1 as="h2">{header.title}</H1>}
          {header.subtitle && <P>{header.subtitle}</P>}
        </S.HeaderContainer>
      )}

      <S.ItemContainer $flip={flip}>
        {(subtitle ||
          title ||
          description ||
          descriptionCtaText ||
          bullets?.length > 0 ||
          button?.children) && (
          <S.InnerContainer>
            {subtitle && <S.Subtitle as="p">{subtitle}</S.Subtitle>}
            {title && <S.Title as="h2">{title}</S.Title>}
            {description && (
              <S.Paragaphs>
                {typeof description === "string" ? (
                  description
                    .split("\n")
                    .map((paragraph, index) => <P key={index}>{paragraph}</P>)
                ) : (
                  <P>
                    <UniversalMarkdown>{description}</UniversalMarkdown>
                  </P>
                )}
              </S.Paragaphs>
            )}

            {descriptionCtaText && (
              <S.Description>
                {descriptionCtaText.map((segment, index) =>
                  segment.href ? (
                    <UniversalLink
                      key={index}
                      href={segment.href}
                    >
                      {segment.label}
                    </UniversalLink>
                  ) : (
                    segment.label
                  )
                )}
              </S.Description>
            )}

            {bullets?.length > 0 && (
              <S.BulletPointContainer>
                {bullets.map((bullet, index) => (
                  <IconBulletPoint
                    key={index}
                    item={{
                      label: bullet.text,
                    }}
                    withIcon={bulletsWithIcon}
                  />
                ))}
              </S.BulletPointContainer>
            )}

            {(button?.children || anchorId) && (
              <S.ButtonsContainer>
                {button?.children && (
                  <Button
                    {...button}
                    size="md"
                    navigation={{
                      href: button.href,
                    }}
                    title={button.title}
                  >
                    {button.children}
                  </Button>
                )}

                {anchorId && (
                  <Button
                    variant="outline"
                    size="md"
                    navigation={{
                      href: "#anchorMenu",
                    }}
                  >
                    Back to top
                  </Button>
                )}
              </S.ButtonsContainer>
            )}
          </S.InnerContainer>
        )}

        {accordionData?.length > 0 && (
          <S.AccordionContainer>
            <Accordion2
              items={accordionData}
              activeColor="primary"
              type="exclusive"
              withArrow={true}
            />
          </S.AccordionContainer>
        )}

        {(linkContent?.length > 0 || accordionHeader) && (
          <S.AccordionContainer>
            {accordionHeader && <S.Title>{accordionHeader}</S.Title>}
            {linkContent.map((link, index) => (
              <UniversalLink
                key={index}
                href={link.href}
                target={link.target}
                title={link.title}
              >
                <S.LinkContentItem>{link.label}</S.LinkContentItem>
              </UniversalLink>
            ))}
          </S.AccordionContainer>
        )}
        {contentType === "quotes"
          ? quotes &&
            quotes?.length > 0 && (
              <S.CarouselWrapper>
                <S.Card>
                  <S.HoverZoneLeft>
                    <S.ArrowLeft
                      onClick={() =>
                        handleArrowClick(
                          current === 0 ? quotes?.length - 1 : current - 1
                        )
                      }
                    >
                      &#10094;
                    </S.ArrowLeft>
                  </S.HoverZoneLeft>
                  <S.QuoteWrapper>
                    <Image
                      src={Quote.src}
                      alt="Quotes"
                      height={44}
                    />
                    <S.QuoteArea>
                      <UniversalLink
                        href={quotes[current]?.link}
                        target="_blank"
                        title={quotes[current]?.quotedPerson}
                      >
                        <Quotes quote={quotes[current]} />
                      </UniversalLink>
                    </S.QuoteArea>
                    <S.Dots>
                      {quotes.map((_, i) => (
                        <S.Dot
                          key={i}
                          $active={i === current}
                          onClick={() => setCurrent(i)}
                        />
                      ))}
                    </S.Dots>
                  </S.QuoteWrapper>
                  <S.HoverZoneRight>
                    <S.ArrowRight
                      onClick={() =>
                        handleArrowClick((current + 1) % quotes?.length)
                      }
                    >
                      &#10095;
                    </S.ArrowRight>
                  </S.HoverZoneRight>
                </S.Card>
              </S.CarouselWrapper>
            )
          : contentType === "video"
            ? videoUrl && (
                <S.VideoInnerContainer>
                  <VideoFrame
                    src={videoUrl}
                    small
                  />
                </S.VideoInnerContainer>
              )
            : image?.src && (
                <S.InnerContainer>
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={900}
                    height={600}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </S.InnerContainer>
              )}
      </S.ItemContainer>
    </S.ItemWrapper>
  );
};

export default BasicSectionComponent;
